import React, {createContext, PropsWithChildren, useMemo} from 'react';
import {Organization, AccountOrganization, AccountOrganizationRole} from '../models';
import {
  OrganizationService,
  OrganizationServiceImpl,
  OrganizationCreateParams,
  AccountOrganizationService,
  AccountOrganizationServiceImpl,
  AccountOrganizationCreateParams,
  OrganizationSearchParams,
} from '../services';
import {useAuth} from './auth.provider';

export interface OrganizationProviderInterface {
  organizationService: OrganizationService;
  accountOrganizationService: AccountOrganizationService;
  createOrganization: (params: {name: string; logoImg?: string; portalUrl?: string}) => Promise<Organization>;
  searchOrganizations: (subdomain: string) => Promise<Organization[]>;
  updateOrganization: (organizationId: string, updates: Partial<Organization>) => Promise<Organization>;
  createAccountOrganization: (accountId: string, organizationId: string, role?: AccountOrganizationRole) => Promise<AccountOrganization>;
}

export const OrganizationContext = createContext<OrganizationProviderInterface>(undefined as any as OrganizationProviderInterface);

export const OrganizationProvider: React.FC<PropsWithChildren<{}>> = ({children}) => {
  const organizationService: OrganizationService = useMemo(() => new OrganizationServiceImpl(), []);
  const accountOrganizationService: AccountOrganizationService = useMemo(() => new AccountOrganizationServiceImpl(), []);
  const {user} = useAuth();

  const createOrganization = async (params: {name: string; logoImg?: string; portalUrl?: string}): Promise<Organization> => {
    if (!user?.id) {
      throw new Error('Account ID not found in context');
    }
    const createParams = new OrganizationCreateParams(params.name, user.id, params.logoImg, params.portalUrl);
    return organizationService.createOrganization(createParams);
  };

  const searchOrganizations = async (subdomain: string): Promise<Organization[]> => {
    const params = new OrganizationSearchParams({subdomain});
    return organizationService.searchOrganizations(params);
  };

  const updateOrganization = async (organizationId: string, updates: Partial<Organization>): Promise<Organization> => {
    if (!user?.id) {
      throw new Error('Account ID not found in context');
    }
    return organizationService.updateOrganization(organizationId, user.id, updates);
  };

  const createAccountOrganization = async (
    accountId: string,
    organizationId: string,
    role: AccountOrganizationRole = AccountOrganizationRole.ADMIN,
  ): Promise<AccountOrganization> => {
    const createParams = new AccountOrganizationCreateParams(accountId, organizationId, role);
    return accountOrganizationService.createAccountOrganization(createParams);
  };

  return (
    <OrganizationContext.Provider
      value={{
        organizationService,
        accountOrganizationService,
        createOrganization,
        searchOrganizations,
        updateOrganization,
        createAccountOrganization,
      }}>
      {children}
    </OrganizationContext.Provider>
  );
};
